import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
}
export const ParticleBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let particles: Particle[] = [];
    let animationFrameId: number;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const particleCount = prefersReducedMotion ? 40 : 110;
    const connectionDistance = 140;
    const setSize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    const initParticles = () => {
      particles = [];
      const w = window.innerWidth;
      const h = window.innerHeight;
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          radius: Math.random() * 1.5 + 0.6
        });
      }
    };
    const handleResize = () => {
      setSize();
      initParticles();
    };
    const draw = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      ctx.clearRect(0, 0, w, h);
      // Update and draw particles
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        // Glowing dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = '#00CFFF';
        ctx.shadowColor = '#00CFFF';
        ctx.shadowBlur = 8;
        ctx.fill();
      });
      // Reset shadow for lines (perf)
      ctx.shadowBlur = 0;
      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const p1 = particles[i];
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < connectionDistance) {
            const opacity = 0.35 * (1 - distance / connectionDistance);
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0, 207, 255, ${opacity})`;
            ctx.lineWidth = 0.6;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(draw);
    };
    setSize();
    initParticles();
    draw();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <div aria-hidden="true" className="fixed inset-0 pointer-events-none overflow-hidden" style={{
    zIndex: 0
  }} data-id="element-120">
      {/* Solid base background */}
      <div className="absolute inset-0 bg-dark-bg" data-id="element-121" />

      {/* Parallax blurred glow blobs */}
      <motion.div animate={{
      x: [0, 60, -40, 0],
      y: [0, -50, 40, 0]
    }} transition={{
      duration: 22,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-neon-blue/15 rounded-full blur-[120px]" data-id="element-122" />
      <motion.div animate={{
      x: [0, -70, 50, 0],
      y: [0, 60, -50, 0]
    }} transition={{
      duration: 28,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-electric-purple/15 rounded-full blur-[150px]" data-id="element-123" />
      <motion.div animate={{
      x: [0, 40, -60, 0],
      y: [0, 30, -40, 0]
    }} transition={{
      duration: 30,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[100px]" data-id="element-124" />

      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" data-id="element-125" />
    </div>;
};